import React, { useState, useEffect, useRef } from 'react'
import _ from 'lodash';
import { Transition } from '@headlessui/react';
import { useClickAway } from 'react-use';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import dayjs from 'dayjs';
import {
  Modal,
  Descriptions,
  Button,
  InputNumber,
  Input as AntdInput,
  Select,
  Switch,
  Popconfirm,
  Collapse,
  Dropdown,
  Menu,
  Tabs,
  Input,
  Avatar,
  DatePicker
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DescriptionsItem from 'antd/lib/descriptions/Item';
import Icon, { CopyOutlined, UserOutlined, PhoneOutlined } from '@ant-design/icons'; 
import { faEnvelope, faCopy, faUser, faPhone, faSearch, faMap, faEye, faEyeSlash } from '@fortawesome/pro-duotone-svg-icons';
import {
  notificationsAtom,
  createNewLeadAtom,
} from '../../../../../atoms/Atoms';
import {
  prMunicipalities,
  ageGroups,
  salaryGroups,
  averageMonthlyPaymentGroups,
  creditScoreGroups,
  roofTypes,
  mapContainerStyle,
  batteryBrands,
  dealTypes,
  paymentTypes,
  contactTypes,
  installers
} from '../../../../../utils';
// import Input from '../../../../shared-components/form/Input';
import PhoneInput from '../../../../shared-components/form/PhoneInput';
import api from '../../../../../api/api';
import { checkNumber, getMobileDatePickerFooter, thousandAutoDecimal, toFormattedLocalTime } from '../../../../../functions';
import envConfig from '../../../../../envConfig';

function CreateNewLeadModal() {
  const defaultLeadData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '+1',
    country: 'PR',
    municipality: prMunicipalities[0],
    ageGroup: ageGroups[0].id,
    salaryGroup: salaryGroups[0].id,
    isPropertyOwner: false,
    averageMonthlyPaymentGroup: averageMonthlyPaymentGroups[0].id,
    creditScoreGroup: creditScoreGroups[0].id,
    roofType: roofTypes[2].id,
    dealType: dealTypes[0].id,
    paymentType: paymentTypes[0].id,
    location: {
      lat: 18.3977264,
      lon: -66.1040551
    },
    installer: installers[0].id
  };
  const { Option } = Select;
  const { TabPane } = Tabs;
  const { Panel } = Collapse;
  const suggestionsRef = useRef();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [createNewLead, setCreateNewLead] = useRecoilState(createNewLeadAtom);
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [state, setState] = useState({
    draggable: true,
    suggestions: [],
  });
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState([]);
  const [mapOpen, setMapOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('1');
  const [loadingUserOrg, setLoadingUserOrg] = useState(false);
  const [userOrgs, setUserOrgs] = useState([]);
  const [selectedUserOrg, setSelectedUserOrg] = useState('');
  const defaultZoom = 11;
  const [showPassword, setShowPassword] = useState({
    password: false,
    ssn: false
  });

  function searchUsers() {
    if (searchText?.length > 0) {
      setSearchingUsers(true);
      api
        .post('admin/users/search', { Query: searchText, Page: 0, Size: 10 })
        .then((response) => {
          setSearchingUsers(false);
          if (response?.data?.success) {
            setUsers(response.data.data.users);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response?.data?.error,
                error: true,
              },
            ]);
          }
        })
        .catch((error) => {
          setSearchingUsers(false);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        });
    } else {
      setUsers([]);
    }
  }

  function getUserOrgs() {
    setLoadingUserOrg(true);
    api.get('organizations/user')
    .then((response) => {
      setLoadingUserOrg(false);
      if (response.data.success) {
        setUserOrgs(response.data.data);
        if (response.data.data?.length === 1) {
          setSelectedUserOrg(response.data.data[0].referralCode);
        }
      }
    })
    .catch((error) => {
      setLoadingUserOrg(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        },
      ]);
    });
  }

  function create() {
    setLoading(true);
    api.post('leads/onbehalf', {
      ...leadData,
      OrgReferralCode: selectedUserOrg,
    })
      .then((response) => {
        setLoading(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('success'),
              description: response.data.message,
              error: false,
              open: false
            },
          ]);
          setCreateNewLead({
            ...createNewLead,
            reload: true,
            open: false
          })
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: envConfig.GoogleApiKey,
    // language: `${i18n.language}-US`,
    language: 'en-US',
    libraries: ['places']
  });

  const apiHasLoaded = (map) => {
    const placesService = new window.google.maps.places.PlacesService(map);
    const autocompleteService = new window.google.maps.places.AutocompleteService();
    if (leadData?.location?.lat && leadData?.location?.lon) {
      const bounds = new window.google.maps.LatLngBounds({
        lat: leadData?.location?.lat,
        lng: leadData?.location?.lon,
      });
      map.fitBounds(bounds);
      map.setZoom(defaultZoom);
    }
    setState({
      ...state,
      mapApiLoaded: true,
      mapInstance: map,
      placesService,
      autocompleteService,
    });
    setAddress('');
  };

  const fetchPredictions = () => {
    if (address.length && state.mapApiLoaded) {
      state.autocompleteService.getPlacePredictions(
        {
          input: address,
          componentRestrictions: { country: 'pr' },
        },
        (predictions, status) => {
          if (status !== 'OK') {
            return;
          }

          setState({
            ...state,
            suggestions: predictions,
          });
        }
      );
    } else {
      setState({
        ...state,
        suggestions: [],
      });
    }
  };

  const getDetails = (placeId) => {
    state.placesService.getDetails(
      {
        placeId,
        fields: [
          'name',
          'address_components',
          'formatted_address',
          'geometry',
          'place_id',
        ],
      },
      (result, status) => {
        if (status === 'OK') {
          state.mapInstance.fitBounds(result.geometry.viewport);
          _.each(result.address_components, (component) => {
            if (component.types[0] === 'locality') {
              const muns = _.filter(prMunicipalities, (mun) => mun === component.long_name);
              if (muns?.length > 0) {
                return setLeadData({
                  ...leadData,
                  municipality: component.long_name
                });
              }
            }
            return null;
          });
          setLeadData({
            ...leadData,
            location: {
              ...leadData?.location,
              lat: result.geometry.location.lat(),
              lon: result.geometry.location.lon()
            }
          });
          setState({
            ...state,
            draggable: true,
            suggestions: [],
          });
          setAddress('');
        }
      }
    );
  };

  const setMunicipalityCoords = () => {
    state.autocompleteService.getPlacePredictions(
      {
        input: leadData?.municipality,
        componentRestrictions: { country: 'pr' },
      },
      (predictions, predictionStatus) => {
        if (predictionStatus !== 'OK') {
          return;
        }
        if (predictions?.length > 0) {
          const placeId = predictions[0].place_id;
          state.placesService.getDetails(
            {
              placeId,
              fields: [
                'name',
                'address_components',
                'formatted_address',
                'geometry',
                'place_id',
              ],
            },
            (detailsResult, detailsStatus) => {
              if (detailsStatus === 'OK') {
                state.mapInstance.fitBounds(detailsResult.geometry.viewport);
                setLeadData({
                  ...leadData,
                  location: {
                    ...leadData?.location,
                    lat: detailsResult.geometry.location.lat(),
                    lon: detailsResult.geometry.location.lng()
                  }
                });
              }
            }
          );
        }
      }
    );
  };

  useEffect(() => {
    fetchPredictions();
  }, [address]);

  useEffect(() => {
    if (leadData?.municipality && state.mapApiLoaded) {
      setMunicipalityCoords();
    }
  }, [leadData?.municipality]);

  useEffect(() => {
    setUserOrgs([]);
    setSelectedUserOrg('');
    if (createNewLead?.open) {
      setAddress('');
      setSelectedTab('1');
      setMapOpen(false);
      setSearchText('');
      setUsers([]);
      setLeadData(defaultLeadData);
      getUserOrgs();
    } else {
      setLeadData(null);
    }
  }, [createNewLead?.open]);

  useClickAway(suggestionsRef, () => {
    setState({
      ...state,
      suggestions: [],
    });
  });

  return (
    <Modal
      title={t('create_new_lead')}
      width="100%"
      zIndex={999}
      open={createNewLead?.open}
      onCancel={() => {
        setCreateNewLead({
          ...createNewLead,
          open: false,
          reload: false
        });
      }}
      footer={[
        <Button
          key="cancel"
          danger
          type="primary"
          disabled={loading}
          onClick={() => setCreateNewLead({
            ...createNewLead,
            open: false,
            reload: false
          })}
        >
          {t('close')}
        </Button>,
        <Popconfirm
          title={t('are_you_sure_create_lead_prompt')}
          okText={t('yes')}
          cancelText={t('no')}
          onConfirm={() => create()}
        >
          <Button
            key="ok"
            type="primary"
            loading={loading}
          >
            {t('submit')}
          </Button>
        </Popconfirm>
      ]}
    >
      {(leadData && leadData !== null) && (
        <>
          <Tabs
            activeKey={selectedTab}
            onChange={(e) => setSelectedTab(e)}
            className="min-h-3/4"
          >
           {/* Tab 1: Deal Workflow */}
           <TabPane tab={t('contact_info')} key="1">
             <Dropdown
               trigger="click"
               placement="bottom"
               overlay={
                 <Menu>
                   {_.map(users, (u) => (
                     <Popconfirm
                       title={t('assign_user_to_lead_prompt')}
                       okText={t('yes')}
                       cancelText={t('no')}
                       onConfirm={() => {
                         setLeadData({
                           ...leadData,
                           firstName: u.firstName,
                           lastName: u.lastName,
                           email: u.email,
                           phone: u.phone
                         });
                         setSearchText('');
                         setUsers([]);
                       }}
                     >
                       <Menu.Item
                         key={u.id}
                       >
                        <span>
                          <Avatar src={u.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                          {u.completeName}: {u.email}
                        </span>
                       </Menu.Item>
                     </Popconfirm>
                   ))}
                 </Menu>
               }
             >
               <AntdInput.Search
                 placeholder={`${t('search_users')}...`}
                 allowClear
                 className="mt-4 col-span-1 md:col-span-2 lg:col-span-4"
                 disabled={loading}
                 loading={searchingUsers}
                 enterButton={t('search')}
                 size="small"
                 value={searchText}
                 onChange={(obj) => {
                   setSearchText(obj.target.value);
                   if (obj.target.value.length === 0) {
                     setUsers([]);
                   }
                 }}
                 onClear={() => setUsers([])}
                 onSearch={(val) => searchUsers()}
               />
             </Dropdown>
             <Descriptions bordered>   
               <DescriptionsItem label={t('name')}>
                 <Input
                   value={leadData?.firstName}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     firstName: obj.target.value
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('last_name')}>
                 <Input
                   value={leadData?.lastName}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     lastName: obj.target.value
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('email')}>
                 <Input
                   value={leadData?.email}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     email: obj.target.value
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('phone')}>
                 <PhoneInput
                   type="phone"
                   name="phone"
                   id="phone"
                   className="h-12"
                   disabled={loading}
                   autoComplete="phone"
                   value={leadData?.phone}
                   onChange={(number) => {
                     setLeadData({
                       ...leadData,
                       phone: number
                     });
                   }}
                 />  
               </DescriptionsItem>
               {/* <DescriptionsItem label={t('deal_id')}>
                 <Input
                   value={leadData?.externalDealId}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     externalDealId: obj.target.value
                   })}
                 />
               </DescriptionsItem> */}
               <DescriptionsItem label={t('referred_by_customer')}>
                 <Switch
                   checked={leadData?.referredByCustomer}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     referredByCustomer: obj
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('referral_code')}>
                 <Select
                   showSearch
                   className="w-full h-12"
                   filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                   value={selectedUserOrg}
                   loading={loadingUserOrg}
                   disabled={loadingUserOrg || userOrgs?.length === 0 || userOrgs?.length === 1}
                   onChange={(obj) => setSelectedUserOrg(obj)}
                 >
                   <Option value="">{t('no_referral')}</Option>
                   {_.map(userOrgs, (org) => (
                     <Option value={org.referralCode}>{org.nameEn}</Option>
                   ))}
                 </Select>
               </DescriptionsItem>
             </Descriptions>
           </TabPane>
           
           {/* Tab 2: Location */}
           <TabPane tab={t('location_info')} key="2">
             <Descriptions bordered>   
               <DescriptionsItem span={2} label={t('location')}>
                 <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                   <div className="col-span-1">
                     <span>{t('latitude')}</span>
                     <div className="flex">
                       <InputNumber
                         value={leadData?.location.lat}
                         disabled={loading}
                         className="w-full"
                         step="0.01"
                         formatter={(value) => thousandAutoDecimal(value)}
                         parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                         onChange={(obj) => setLeadData({
                           ...leadData,
                           location: {
                             ...leadData.location,
                             lat: obj
                           }
                         })}
                       />
                       <Button
                         className="ml-2"
                         icon={<CopyOutlined />}
                         onClick={() => navigator.clipboard.writeText(leadData?.location.lat)}
                       />
                     </div>
                   </div>
                   <div className="col-span-1">
                     <span>{t('longitude')}</span>
                     <div className="flex">
                       <InputNumber
                         value={leadData?.location.lon}
                         disabled={loading}
                         className="w-full"
                         step="0.01"
                         formatter={(value) => thousandAutoDecimal(value)}
                         parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                         onChange={(obj) => setLeadData({
                           ...leadData,
                           location: {
                             ...leadData.location,
                             lon: obj
                           }
                         })}
                       />
                       <Button
                         className="ml-2"
                         icon={<CopyOutlined />}
                         onClick={() => navigator.clipboard.writeText(leadData?.location.lon)}
                       />
                     </div>
                   </div>
                   <Collapse activeKey={mapOpen ? ['1'] : []} onChange={() => setMapOpen(!mapOpen)} className="col-span-1 md:col-span-2">
                     <Panel
                       key="1"
                       header={(
                         <div className="flex space-x-1">
                           <FontAwesomeIcon className="mt-1" icon={faMap} />
                           <span>{t('map')}</span>
                         </div>
                       )}
                     >
                       <div className="relative text-center mt-4">
                         <span>{t('move_marker_on_map_or_search')}</span>
                         <div className="flex">
                           <Input
                             icon={faSearch}
                             value={address}
                             onChange={(obj) => setAddress(obj.target.value)}
                           />
                         </div>
                         <Transition
                           appear={state.suggestions.length > 0}
                           show={state.suggestions.length > 0}
                           enter="transform transition duration-500"
                           enterFrom="opacity-0 -translate-y-50"
                           enterTo="opacity-100 -translate-y-0"
                           leave="transform transition duration-150"
                           leaveFrom="opacity-100"
                           leaveTo="opacity-0"
                           className="absolute z-10 mt-2 w-full divide-y divide-gray-100 shadow-xl bg-white px-4"
                         >
                           {_.map(state.suggestions, (suggestion) => (
                             <button
                               key={suggestion.place_id}
                               type="button"
                               onClick={() => getDetails(suggestion.place_id)}
                               className="text-gray-400 hover:text-gray-500 text-xs text-left block w-full py-4 px-4 truncate ring-0 focus:ring-0 outline-none focus:outline-none transition duration-200 ease-linear cursor-pointer"
                             >
                               {suggestion.description}
                             </button>
                           ))}
                         </Transition>
                         <GoogleMap
                           mapContainerStyle={mapContainerStyle}
                           zoomControl
                           draggable
                           mapTypeId="satellite"
                           center={{
                             lat: leadData?.location?.lat,
                             lng: leadData?.location?.lon,
                           }}
                           zoom={defaultZoom}
                           onLoad={apiHasLoaded}
                         >
                           <Marker
                             draggable
                             position={{ lat: leadData?.location?.lat, lng: leadData?.location?.lon }}
                             onDragEnd={(coords) => {
                               const lat = coords.latLng.lat();
                               const lon = coords.latLng.lng();
                               setLeadData({
                                 ...leadData,
                                 location: {
                                   ...leadData.location,
                                   lon,
                                   lat
                                 }
                               });
                             }}
                           />
                         </GoogleMap>
                       </div>
                     </Panel>
                   </Collapse>
                 </div>
               </DescriptionsItem>
               <DescriptionsItem label={t('address')}>
                <div className="space-y-2">
                  <Input
                    className="p-2"
                    value={leadData?.addressLine1}
                    disabled={loading}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      addressLine1: obj.target.value
                    })}
                  />
                  <Input
                    className="p-2"
                    value={leadData?.addressLine2}
                    disabled={loading}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      addressLine2: obj.target.value
                    })}
                  />
                </div>
               </DescriptionsItem>
               <DescriptionsItem label={t('municipality')}>
                 <Select
                   showSearch
                   className="w-full h-12"
                   filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                   value={leadData?.municipality}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     municipality: obj
                   })}
                 >
                   {_.map(prMunicipalities, (mun) => (
                     <Option value={mun}>{mun}</Option>
                   ))}
                 </Select>
               </DescriptionsItem>
               <DescriptionsItem label={t('country')}>
                 <Input
                   value={leadData?.country}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     country: obj.target.value
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('zipcode')}>
                 <Input
                   value={leadData?.zipcode}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     zipcode: obj.target.value
                   })}
                 />
               </DescriptionsItem>
             </Descriptions>
           </TabPane>

           {/* Tab 3: System */}
           <TabPane tab={t('system')} key="3">   
             <Descriptions className="flex flex-col grid grid-cols-1" bordered>     
               <DescriptionsItem label={t('gross_deal_amount')}>
                 <InputNumber
                   value={leadData?.grossDealAmount}
                   disabled={loading}
                   min={0}
                   className="w-full"
                   step="0.01"
                   addonBefore="$"
                   formatter={(value) => thousandAutoDecimal(value)}
                   parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     grossDealAmount: checkNumber(obj, 2)
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('deal_amount')}>
                 <InputNumber
                   value={leadData?.dealAmount}
                   disabled={loading}
                   min={0}
                   className="w-full"
                   step="0.01"
                   addonBefore="$"
                   formatter={(value) => thousandAutoDecimal(value)}
                   parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     dealAmount: checkNumber(obj, 2)
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('panel_size')}>
                 <div className="flex gap-2">
                   <InputNumber
                     value={leadData?.panelSize}
                     disabled={loading}
                     min={0}
                     className="w-full"
                     step="0.01"
                     formatter={(value) => thousandAutoDecimal(value)}
                     parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                     onChange={(obj) => setLeadData({
                       ...leadData,
                       panelSize: checkNumber(obj, 2)
                     })}
                   />
                 </div>
               </DescriptionsItem>
               <DescriptionsItem label={t('panel_amount')}>
                 <div className="flex gap-2">
                   <InputNumber
                     value={leadData?.panelAmount}
                     disabled={loading}
                     min={0}
                     className="w-full"
                     step="1"
                     formatter={(value) => thousandAutoDecimal(value)}
                     parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                     onChange={(obj) => setLeadData({
                       ...leadData,
                       panelAmount: checkNumber(obj, 0)
                     })}
                   />
                 </div>
               </DescriptionsItem>
               <DescriptionsItem label={t('system_size')}>
                 <div className="flex gap-2">
                   {/* <Button
                     type="primary"
                     onClick={() => setSizeCalculator(true)}
                   >
                    {t('size_calculator')}
                   </Button> */}
                   <InputNumber
                     value={leadData?.systemSize}
                     disabled={loading}
                     min={0}
                     className="w-full"
                     step="0.01"
                     formatter={(value) => thousandAutoDecimal(value)}
                     parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                     onChange={(obj) => setLeadData({
                       ...leadData,
                       systemSize: checkNumber(obj, 2)
                     })}
                   />
                 </div>
               </DescriptionsItem>
               <DescriptionsItem label={t('battery_brand')}>
                  <Select
                    showSearch
                    className="w-full h-12"
                    filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                    value={leadData?.batteryBrand}
                    disabled={loading}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      batteryBrand: obj
                    })}
                  >
                    {_.map(batteryBrands, (bb) => (
                      <Option value={bb.name}>{bb.name}</Option>
                    ))}
                  </Select>
               </DescriptionsItem>
               <DescriptionsItem label={t('batteries_amount')}>
                <InputNumber
                  value={leadData?.batteriesAmount}
                  disabled={loading}
                  min={0}
                  className="w-full"
                  step="1"
                  formatter={(value) => thousandAutoDecimal(value)}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  onChange={(obj) => setLeadData({
                    ...leadData,
                    batteriesAmount: checkNumber(obj, 0)
                  })}
                />
               </DescriptionsItem>
               <DescriptionsItem label={t('payment_type')}>
                 <Select
                   className="w-full h-12"
                   value={leadData?.paymentType}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     paymentType: obj
                   })}
                 >
                   {_.map(paymentTypes, (g) => (
                     <Option value={g.id}>{t(g.name)}</Option>
                   ))}
                 </Select>
               </DescriptionsItem> 
               <DescriptionsItem label={t('payment_amount')}>
                <InputNumber
                  value={leadData?.paymentAmount}
                  disabled={loading}
                  min={0}
                  className="w-full"
                  step="0.01"
                  addonBefore="$"
                  formatter={(value) => thousandAutoDecimal(value)}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  onChange={(obj) => setLeadData({
                    ...leadData,
                    paymentAmount: checkNumber(obj, 2)
                  })}
                />
               </DescriptionsItem>
               <DescriptionsItem label={t('deal_id')}>
                 <Input
                   value={leadData?.externalDealId}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     externalDealId: obj.target.value
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('installer')}>
                 <Select
                   showSearch
                   className="w-full h-12"
                   filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                   value={leadData.contactType}
                   loading={loading}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                    ...leadData,
                    installer: obj
                   })}
                 >
                   {_.map(installers, (c) => (
                     <Option value={c.id}>{t(c.name)}</Option>
                   ))}
                 </Select>
               </DescriptionsItem>
             </Descriptions>
           </TabPane>

           {/* Tab 4: Survey */}
           <TabPane tab={t('survey_answers')} key="4">
             <Descriptions bordered>
                <DescriptionsItem label={t('deal_type')}>
                  <Select
                    className="w-full h-12"
                    value={leadData?.dealType}
                    disabled={loading}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      dealType: obj
                    })}
                  >
                    {_.map(dealTypes, (g) => (
                      <Option value={g.id}>{t(g.name)}</Option>
                    ))}
                  </Select>
                </DescriptionsItem> 
               <DescriptionsItem label={t('age_group')}>
                 <Select
                   showSearch
                   className="w-full h-12"
                   filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                   value={leadData?.ageGroup}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     ageGroup: obj
                   })}
                 >
                   {_.map(ageGroups, (g) => (
                     <Option value={g.id}>{t(g.name)}</Option>
                   ))}
                 </Select>
               </DescriptionsItem>
               <DescriptionsItem label={t('salary_group')}>
                 <Select
                   showSearch
                   className="w-full h-12"
                   filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                   value={leadData?.salaryGroup}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     salaryGroup: obj
                   })}
                 >
                   {_.map(salaryGroups, (g) => (
                     <Option value={g.id}>{t(g.name)}</Option>
                   ))}
                 </Select>
               </DescriptionsItem>
               <DescriptionsItem label={t('property_owner')}>
                 <Switch
                   checked={leadData?.isPropertyOwner}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     isPropertyOwner: obj
                   })}
                 />
               </DescriptionsItem>
               <DescriptionsItem label={t('monthly_bill')}>
                 <Select
                   showSearch
                   className="w-full h-12"
                   filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                   value={leadData?.averageMonthlyPaymentGroup}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     averageMonthlyPaymentGroup: obj
                   })}
                 >
                   {_.map(averageMonthlyPaymentGroups, (g) => (
                     <Option value={g.id}>{t(g.name)}</Option>
                   ))}
                 </Select>
               </DescriptionsItem>
               <DescriptionsItem label={t('credit_score')}>
                 <Select
                   showSearch
                   className="w-full h-12"
                   filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                   value={leadData?.creditScoreGroup}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     creditScoreGroup: obj
                   })}
                 >
                   {_.map(creditScoreGroups, (g) => (
                     <Option value={g.id}>{t(g.name)}</Option>
                   ))}
                 </Select>
               </DescriptionsItem>
               {/* <DescriptionsItem className="hidden" label={t('roof_type')}>
                 <Select
                   showSearch
                   className="w-full h-12 hidden"
                   filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                   value={leadData?.roofType}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     roofType: obj
                   })}
                 >
                   {_.map(roofTypes, (g) => (
                     <Option value={g.id}>{t(g.name)}</Option>
                   ))}
                 </Select>
               </DescriptionsItem> */}            
             </Descriptions>
           </TabPane>
           
           {/* Tab 5: NEM */}
           <TabPane tab="NEM" key="5">
             <Descriptions bordered>
               <DescriptionsItem label={t('step_one_completion')}>
                 <DatePicker
                   format="YYYY-MM-DD"
                   className="w-full"
                   inputReadOnly
                   onChange={(date) => {
                     setLeadData({
                       ...leadData,
                       nemStepOneDate: date
                     })
                   }}
                   value={leadData?.nemStepOneDate ? dayjs(leadData?.nemStepOneDate) : null}
                   showSecond={false}
                   type="primary"
                   size="small"
                   renderExtraFooter={() => getMobileDatePickerFooter(t)}
                   placement="bottomLeft"
                 />
               </DescriptionsItem>
                <DescriptionsItem label={t('step_two_completion')}>
                  <DatePicker
                    format="YYYY-MM-DD"
                    className="w-full"
                    inputReadOnly
                    onChange={(date) => {
                      setLeadData({
                        ...leadData,
                        nemStepTwoDate: date
                      })
                    }}
                    value={leadData?.nemStepTwoDate ? dayjs(leadData?.nemStepTwoDate) : null}
                    showSecond={false}
                    type="primary"
                    size="small"
                    renderExtraFooter={() => getMobileDatePickerFooter(t)}
                    placement="bottomLeft"
                  />
                </DescriptionsItem>
               <DescriptionsItem label={t('email')}>
                  <Input
                    value={leadData?.nemPortalEmail}
                    disabled={loading}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      nemPortalEmail: obj.target.value
                    })}
                  />
               </DescriptionsItem>
               <DescriptionsItem label={t('username')}>
                  <Input
                    value={leadData?.nemPortalUsername}
                    disabled={loading}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      nemPortalUsername: obj.target.value
                    })}
                  />
               </DescriptionsItem>
               <DescriptionsItem label={t('password')}>
                  <Input
                    value={leadData?.nemPortalPassword}
                    disabled={loading}
                    type={showPassword.password ? 'text' : 'password'}
                    suffix={(
                      <button
                        type="button"
                        onClick={() => setShowPassword({
                          ...showPassword,
                          password: !showPassword.password
                        })}
                      >
                        <FontAwesomeIcon icon={showPassword.password ? faEye : faEyeSlash} />
                      </button>
                    )}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      nemPortalPassword: obj.target.value
                    })}
                  />
               </DescriptionsItem>
               <DescriptionsItem label={t('ssn_last_4')}>
                  <Input
                    value={leadData?.nemLast4SSN}
                    type={showPassword.ssn ? 'text' : 'password'}
                    suffix={(
                      <button
                        type="button"
                        onClick={() => setShowPassword({
                          ...showPassword,
                          ssn: !showPassword.ssn
                        })}
                      >
                        <FontAwesomeIcon icon={showPassword.ssn ? faEye : faEyeSlash} />
                      </button>
                    )}
                    disabled={loading}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      nemLast4SSN: obj.target.value
                    })}
                  />
               </DescriptionsItem>
               <DescriptionsItem label={t('job')}>
                  <Input
                    value={leadData?.nemJob}
                    type="password"
                    disabled={loading}
                    onChange={(obj) => setLeadData({
                      ...leadData,
                      nemJob: obj.target.value
                    })}
                  />
               </DescriptionsItem>
             </Descriptions>
           </TabPane>

           {/* Tab 6: Other */}
           <TabPane tab={t('other')} key="6">
             <Descriptions bordered> 
               <DescriptionsItem label={t('notes')}>
                 <Input
                   value={leadData?.notes}
                   disabled={loading}
                   onChange={(obj) => setLeadData({
                     ...leadData,
                     notes: obj.target.value
                   })}
                 />
               </DescriptionsItem>           
             </Descriptions>
           </TabPane>
          </Tabs>
        </>
      )}
    </Modal>
  );
}

export default CreateNewLeadModal;
